/**
 * RevFinOps API Service Layer
 * Provides centralized API access for all RevFinOps backend endpoints
 * Uses AWS Amplify for IAM-authenticated HTTP requests
 */

import { get, post, put, del } from 'aws-amplify/api'
import { createApiHeaders, logApiCall } from './services/api-utils'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * RevFinOps Client entity
 */
export interface RevFinOpsClient {
  id: string
  name: string
  status: 'active' | 'inactive' | 'pending'
  email?: string
  phone?: string
  address?: string
  taxId?: string
  contactPerson?: string
  notes?: string
  createdAt: string
  updatedAt: string
}

/**
 * RevFinOps Payable entity
 */
export interface RevFinOpsPayable {
  id: string
  clientId?: string
  clientName?: string
  amount: number
  currency: string
  description?: string
  status: 'pending' | 'approved' | 'scheduled' | 'paid' | 'returned'
  dueDate: string
  approvedBy?: string
  approvedDate?: string
  scheduledDate?: string
  paidDate?: string
  returnedDate?: string
  returnReason?: string
  invoiceNumber?: string
  reference?: string
  createdAt: string
  updatedAt: string
}

/**
 * RevFinOps Assignment entity
 */
export interface RevFinOpsAssignment {
  id: string
  personId: string
  personName?: string
  sowId: string
  sowName?: string
  payRate: number
  currency?: string
  startDate: string
  endDate?: string
  status: 'active' | 'inactive' | 'completed'
  workload?: number
  role?: string
  notes?: string
  createdAt: string
  updatedAt: string
}

/**
 * Assignment history entry
 */
export interface AssignmentHistoryEntry {
  id: string
  assignmentId: string
  eventType: 'created' | 'rate_increased' | 'updated' | 'completed' | 'cancelled'
  previousPayRate?: number
  newPayRate?: number
  changedBy: string
  changeReason?: string
  timestamp: string
  metadata?: Record<string, any>
}

/**
 * Usage Report entity for ingestion
 */
export interface UsageReport {
  id: string
  sowId: string
  sowName?: string
  reportDate: string
  hours?: number
  units?: number
  description?: string
  status: 'pending' | 'processed' | 'invoiced'
  source: 'gmail' | 'manual' | 'api'
  rawData?: Record<string, any>
  createdAt: string
  updatedAt: string
}

/**
 * Filters for payables list
 */
export interface PayableFilters {
  status?: RevFinOpsPayable['status']
  clientId?: string
  fromDate?: string
  toDate?: string
  minAmount?: number
  maxAmount?: number
}

/**
 * Standard API response wrapper
 */
export interface APIResponse<T> {
  data?: T
  error?: APIError
  success: boolean
  message?: string
}

/**
 * API Error details
 */
export interface APIError {
  message: string
  code?: string
  details?: any
  statusCode?: number
}

/**
 * Job response for async operations
 */
export interface JobResponse {
  jobId: string
  status: 'queued' | 'processing' | 'completed' | 'failed'
  message?: string
}

// ============================================================================
// PRIVATE HELPER FUNCTIONS
// ============================================================================

/**
 * Call RevFinOps API endpoint with proper headers and error handling
 * @private
 */
async function callRevFinOpsAPI<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  data?: any
): Promise<APIResponse<T>> {
  const apiName = 'monumenta' // Using the monumenta API endpoint
  const fullPath = `/revfinops${path}`

  logApiCall(fullPath)

  try {
    let apiCall

    switch (method) {
      case 'GET':
        apiCall = get({
          apiName,
          path: fullPath,
          options: {
            headers: createApiHeaders()
          }
        })
        break
      case 'POST':
        apiCall = post({
          apiName,
          path: fullPath,
          options: {
            headers: createApiHeaders(),
            body: data
          }
        })
        break
      case 'PUT':
        apiCall = put({
          apiName,
          path: fullPath,
          options: {
            headers: createApiHeaders(),
            body: data
          }
        })
        break
      case 'DELETE':
        apiCall = del({
          apiName,
          path: fullPath,
          options: {
            headers: createApiHeaders()
          }
        })
        break
    }

    const { response } = await apiCall
    const result = await response
    const responseData = await result.body.json() as any

    return {
      success: true,
      data: responseData as T,
      message: responseData?.message
    }
  } catch (error: any) {
    console.error(`RevFinOps API Error [${method} ${fullPath}]:`, error)

    return {
      success: false,
      error: {
        message: error?.message || 'An error occurred while calling the API',
        code: error?.code || 'UNKNOWN_ERROR',
        details: error,
        statusCode: error?.response?.statusCode
      }
    }
  }
}

// ============================================================================
// CLIENT MANAGEMENT API FUNCTIONS
// ============================================================================

/**
 * Create a new RevFinOps client
 *
 * @param clientData - Client information (partial, ID will be generated by backend)
 * @returns Promise resolving to created client or error
 *
 * @example
 * ```typescript
 * const result = await createClient({
 *   name: 'Acme Corp',
 *   email: 'contact@acme.com',
 *   status: 'active'
 * })
 *
 * if (result.success) {
 *   console.log('Client created:', result.data)
 * } else {
 *   console.error('Error:', result.error.message)
 * }
 * ```
 */
export async function createClient(
  clientData: Partial<RevFinOpsClient>
): Promise<APIResponse<RevFinOpsClient>> {
  return await callRevFinOpsAPI<RevFinOpsClient>('POST', '/clients', clientData)
}

/**
 * List all RevFinOps clients
 *
 * @returns Promise resolving to array of clients or error
 *
 * @example
 * ```typescript
 * const result = await listClients()
 *
 * if (result.success && result.data) {
 *   console.log('Clients:', result.data)
 * }
 * ```
 */
export async function listClients(): Promise<APIResponse<RevFinOpsClient[]>> {
  return await callRevFinOpsAPI<RevFinOpsClient[]>('GET', '/clients')
}

/**
 * Get a specific client by ID
 *
 * @param id - Client ID
 * @returns Promise resolving to client details or error
 *
 * @example
 * ```typescript
 * const result = await getClient('client-123')
 *
 * if (result.success && result.data) {
 *   console.log('Client:', result.data)
 * }
 * ```
 */
export async function getClient(id: string): Promise<APIResponse<RevFinOpsClient>> {
  return await callRevFinOpsAPI<RevFinOpsClient>('GET', `/clients/${id}`)
}

/**
 * Update an existing client
 *
 * @param id - Client ID to update
 * @param clientData - Updated client information (partial)
 * @returns Promise resolving to updated client or error
 *
 * @example
 * ```typescript
 * const result = await updateClient('client-123', {
 *   name: 'Updated Name',
 *   status: 'inactive'
 * })
 *
 * if (result.success) {
 *   console.log('Client updated:', result.data)
 * }
 * ```
 */
export async function updateClient(
  id: string,
  clientData: Partial<RevFinOpsClient>
): Promise<APIResponse<RevFinOpsClient>> {
  return await callRevFinOpsAPI<RevFinOpsClient>('PUT', `/clients/${id}`, clientData)
}

/**
 * Remove a client (soft delete)
 *
 * @param id - Client ID to remove
 * @returns Promise resolving to success/error
 *
 * @example
 * ```typescript
 * const result = await removeClient('client-123')
 *
 * if (result.success) {
 *   console.log('Client removed successfully')
 * } else {
 *   console.error('Error:', result.error.message)
 * }
 * ```
 */
export async function removeClient(id: string): Promise<APIResponse<void>> {
  return await callRevFinOpsAPI<void>('DELETE', `/clients/${id}`)
}

// ============================================================================
// PAYABLES MANAGEMENT API FUNCTIONS
// ============================================================================

/**
 * List payables with optional filters
 *
 * @param filters - Optional filters for status, client, dates, amounts
 * @returns Promise resolving to array of payables or error
 *
 * @example
 * ```typescript
 * const result = await listPayables({ status: 'pending' })
 *
 * if (result.success && result.data) {
 *   console.log('Pending payables:', result.data)
 * }
 * ```
 */
export async function listPayables(
  filters?: PayableFilters
): Promise<APIResponse<RevFinOpsPayable[]>> {
  let path = '/payables'

  if (filters) {
    const queryParams = new URLSearchParams()
    if (filters.status) queryParams.append('status', filters.status)
    if (filters.clientId) queryParams.append('clientId', filters.clientId)
    if (filters.fromDate) queryParams.append('fromDate', filters.fromDate)
    if (filters.toDate) queryParams.append('toDate', filters.toDate)
    if (filters.minAmount !== undefined) queryParams.append('minAmount', filters.minAmount.toString())
    if (filters.maxAmount !== undefined) queryParams.append('maxAmount', filters.maxAmount.toString())

    const queryString = queryParams.toString()
    if (queryString) {
      path += `?${queryString}`
    }
  }

  return await callRevFinOpsAPI<RevFinOpsPayable[]>('GET', path)
}

/**
 * Get a specific payable by ID
 *
 * @param id - Payable ID
 * @returns Promise resolving to payable details or error
 *
 * @example
 * ```typescript
 * const result = await getPayable('payable-123')
 *
 * if (result.success && result.data) {
 *   console.log('Payable:', result.data)
 * }
 * ```
 */
export async function getPayable(id: string): Promise<APIResponse<RevFinOpsPayable>> {
  return await callRevFinOpsAPI<RevFinOpsPayable>('GET', `/payables/${id}`)
}

/**
 * Approve a payable for payment
 *
 * @param id - Payable ID to approve
 * @returns Promise resolving to updated payable or error
 *
 * @example
 * ```typescript
 * const result = await approvePayable('payable-123')
 *
 * if (result.success) {
 *   console.log('Payable approved:', result.data)
 * }
 * ```
 */
export async function approvePayable(id: string): Promise<APIResponse<RevFinOpsPayable>> {
  return await callRevFinOpsAPI<RevFinOpsPayable>('POST', `/payables/${id}/approve`)
}

/**
 * Schedule a payable for a specific payment date
 *
 * @param id - Payable ID to schedule
 * @param date - Scheduled payment date (ISO 8601 format)
 * @returns Promise resolving to updated payable or error
 *
 * @example
 * ```typescript
 * const result = await schedulePayable('payable-123', '2025-10-15')
 *
 * if (result.success) {
 *   console.log('Payable scheduled:', result.data)
 * }
 * ```
 */
export async function schedulePayable(
  id: string,
  date: string
): Promise<APIResponse<RevFinOpsPayable>> {
  return await callRevFinOpsAPI<RevFinOpsPayable>('POST', `/payables/${id}/schedule`, { date })
}

/**
 * Return a payable with a reason
 *
 * @param id - Payable ID to return
 * @param reason - Reason for returning the payable
 * @returns Promise resolving to updated payable or error
 *
 * @example
 * ```typescript
 * const result = await returnPayable('payable-123', 'Incorrect amount')
 *
 * if (result.success) {
 *   console.log('Payable returned:', result.data)
 * }
 * ```
 */
export async function returnPayable(
  id: string,
  reason: string
): Promise<APIResponse<RevFinOpsPayable>> {
  return await callRevFinOpsAPI<RevFinOpsPayable>('POST', `/payables/${id}/return`, { reason })
}

/**
 * Mark a payable as paid
 *
 * @param id - Payable ID to mark as paid
 * @returns Promise resolving to updated payable or error
 *
 * @example
 * ```typescript
 * const result = await markAsPaid('payable-123')
 *
 * if (result.success) {
 *   console.log('Payable marked as paid:', result.data)
 * }
 * ```
 */
export async function markAsPaid(id: string): Promise<APIResponse<RevFinOpsPayable>> {
  return await callRevFinOpsAPI<RevFinOpsPayable>('POST', `/payables/${id}/paid`)
}

// ============================================================================
// ASSIGNMENT MANAGEMENT API FUNCTIONS
// ============================================================================

/**
 * List assignments for a specific SOW
 *
 * @param sowId - Statement of Work ID
 * @returns Promise resolving to array of assignments or error
 *
 * @example
 * ```typescript
 * const result = await listAssignmentsBySow('sow-123')
 *
 * if (result.success && result.data) {
 *   console.log('Assignments:', result.data)
 * }
 * ```
 */
export async function listAssignmentsBySow(
  sowId: string
): Promise<APIResponse<RevFinOpsAssignment[]>> {
  return await callRevFinOpsAPI<RevFinOpsAssignment[]>('GET', `/sows/${sowId}/assignments`)
}

/**
 * Create a new assignment for a SOW
 *
 * @param sowId - Statement of Work ID
 * @param assignmentData - Assignment information
 * @returns Promise resolving to created assignment or error
 *
 * @example
 * ```typescript
 * const result = await createAssignment('sow-123', {
 *   personId: 'person-456',
 *   payRate: 150,
 *   startDate: '2025-10-01',
 *   status: 'active'
 * })
 *
 * if (result.success) {
 *   console.log('Assignment created:', result.data)
 * }
 * ```
 */
export async function createAssignment(
  sowId: string,
  assignmentData: Partial<RevFinOpsAssignment>
): Promise<APIResponse<RevFinOpsAssignment>> {
  return await callRevFinOpsAPI<RevFinOpsAssignment>(
    'POST',
    `/sows/${sowId}/assignments`,
    assignmentData
  )
}

/**
 * Get a specific assignment by ID
 *
 * @param assignmentId - Assignment ID
 * @returns Promise resolving to assignment details or error
 *
 * @example
 * ```typescript
 * const result = await getAssignment('assignment-789')
 *
 * if (result.success && result.data) {
 *   console.log('Assignment:', result.data)
 * }
 * ```
 */
export async function getAssignment(
  assignmentId: string
): Promise<APIResponse<RevFinOpsAssignment>> {
  return await callRevFinOpsAPI<RevFinOpsAssignment>('GET', `/assignments/${assignmentId}`)
}

/**
 * Increase pay rate for an assignment
 *
 * @param assignmentId - Assignment ID
 * @param newRate - New pay rate
 * @returns Promise resolving to updated assignment or error
 *
 * @example
 * ```typescript
 * const result = await increasePayRate('assignment-789', 175)
 *
 * if (result.success) {
 *   console.log('Pay rate increased:', result.data)
 * }
 * ```
 */
export async function increasePayRate(
  assignmentId: string,
  newRate: number
): Promise<APIResponse<RevFinOpsAssignment>> {
  return await callRevFinOpsAPI<RevFinOpsAssignment>(
    'POST',
    `/assignments/${assignmentId}/increase-pay-rate`,
    { newRate }
  )
}

/**
 * Get assignment history (all changes and events)
 *
 * @param assignmentId - Assignment ID
 * @returns Promise resolving to array of history entries or error
 *
 * @example
 * ```typescript
 * const result = await getAssignmentHistory('assignment-789')
 *
 * if (result.success && result.data) {
 *   console.log('History:', result.data)
 * }
 * ```
 */
export async function getAssignmentHistory(
  assignmentId: string
): Promise<APIResponse<AssignmentHistoryEntry[]>> {
  return await callRevFinOpsAPI<AssignmentHistoryEntry[]>(
    'GET',
    `/assignments/${assignmentId}/history`
  )
}

// ============================================================================
// INGESTION API FUNCTIONS
// ============================================================================

/**
 * Trigger manual ingestion of external transactions
 *
 * @returns Promise resolving to job information or error
 *
 * @example
 * ```typescript
 * const result = await triggerManualIngestion()
 *
 * if (result.success && result.data) {
 *   console.log('Ingestion job started:', result.data.jobId)
 * }
 * ```
 */
export async function triggerManualIngestion(): Promise<APIResponse<JobResponse>> {
  return await callRevFinOpsAPI<JobResponse>('POST', '/ingestion/trigger')
}

/**
 * Get usage reports for a specific SOW
 *
 * @param sowId - Statement of Work ID
 * @returns Promise resolving to array of usage reports or error
 *
 * @example
 * ```typescript
 * const result = await getUsageReportsBySow('sow-123')
 *
 * if (result.success && result.data) {
 *   console.log('Usage reports:', result.data)
 * }
 * ```
 */
export async function getUsageReportsBySow(
  sowId: string
): Promise<APIResponse<UsageReport[]>> {
  return await callRevFinOpsAPI<UsageReport[]>('GET', `/sows/${sowId}/usage-reports`)
}

/**
 * Process historical Gmail emails for transaction ingestion
 *
 * @param startDate - Start date for historical processing (ISO 8601 format)
 * @param endDate - End date for historical processing (ISO 8601 format)
 * @returns Promise resolving to job information or error
 *
 * @example
 * ```typescript
 * const result = await processGmailHistorical('2025-09-01', '2025-09-30')
 *
 * if (result.success && result.data) {
 *   console.log('Historical processing job started:', result.data.jobId)
 * }
 * ```
 */
export async function processGmailHistorical(
  startDate: string,
  endDate: string
): Promise<APIResponse<JobResponse>> {
  return await callRevFinOpsAPI<JobResponse>('POST', '/ingestion/gmail/historical', {
    startDate,
    endDate
  })
}

// ============================================================================
// SERVICE INSTANCE
// ============================================================================

/**
 * RevFinOps API Service - Default export with all API functions
 */
export const revFinOpsApi = {
  // Client management
  createClient,
  listClients,
  getClient,
  updateClient,
  removeClient,

  // Payables management
  listPayables,
  getPayable,
  approvePayable,
  schedulePayable,
  returnPayable,
  markAsPaid,

  // Assignment management
  listAssignmentsBySow,
  createAssignment,
  getAssignment,
  increasePayRate,
  getAssignmentHistory,

  // Ingestion
  triggerManualIngestion,
  getUsageReportsBySow,
  processGmailHistorical
}

export default revFinOpsApi
